import { useState } from "react";
import { MapPin } from "lucide-react";
import { api, errMsg, fmtTime } from "@/lib/api";
import { Clock } from "@/components/common";
import { toast } from "sonner";

export default function PunchClock({ initial, onChange }) {
  const [rec, setRec] = useState(initial);
  const [busy, setBusy] = useState(false);
  const state = !rec?.check_in ? "in" : !rec?.check_out ? "out" : "done";
  const onBreak = state === "out" && !!rec?.on_break;
  const toggleBreak = async () => {
    setBusy(true);
    try { const { data } = await api.post("/attendance/break"); setRec(data); toast.success(data.on_break ? "On break. See you soon." : `Back from break · ${data.break_minutes || 0} min total`); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const punch = async () => {
    if (state === "done") return;
    setBusy(true);
    try { const { data } = await api.post("/attendance/punch"); setRec(data); toast.success(state === "in" ? "Punched in. Have a good one." : `Punched out · ${data.hours}h logged`); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="paper-card p-5 bg-gradient-to-br from-card to-secondary/60 flex flex-col justify-between min-h-[180px]" data-testid="punch-clock-widget">
      <div className="flex items-start justify-between">
        <div><div className="eyebrow !text-muted-foreground">Time card</div><div className="font-serif text-2xl font-medium mt-1"><Clock /></div></div>
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground font-mono"><MapPin size={11} /> web</div>
      </div>
      <div className="flex items-end justify-between gap-3 mt-4">
        <div className="text-xs text-muted-foreground font-mono leading-5">
          <div>IN &nbsp;<span className="text-foreground" data-testid="punch-in-time">{fmtTime(rec?.check_in)}</span>{rec?.slot && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-secondary border border-border" data-testid="punch-slot">Slot {rec.slot}</span>}</div>
          <div>OUT <span className="text-foreground" data-testid="punch-out-time">{fmtTime(rec?.check_out)}</span></div>
          {(rec?.break_minutes > 0 || onBreak) && <div data-testid="break-status" className={onBreak ? "text-amber" : ""}>{onBreak ? "ON BREAK" : "BREAK"} <span className="text-foreground">{rec.break_minutes || 0} min</span></div>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <button data-testid="attendance-punch-button" disabled={busy || state === "done" || onBreak} onClick={punch} className={`punch-btn h-12 px-6 ${state === "out" ? "out" : ""} ${state === "done" ? "done" : ""}`}>
            {state === "in" ? "Punch in" : state === "out" ? "Punch out" : `${rec.hours ?? 0}h logged`}
          </button>
          {state === "out" && <button data-testid="attendance-break-button" disabled={busy} onClick={toggleBreak} className={`h-8 px-4 rounded-full text-xs font-medium border transition-colors ${onBreak ? "bg-amber/15 border-amber text-amber" : "border-border bg-card hover:border-primary/60"}`}>{onBreak ? "Break in (resume)" : "Break out"}</button>}
        </div>
      </div>
    </div>
  );
}
